/**
 * PlanCard Component
 *
 * Displays a subscription plan with pricing, features, and selection state.
 * Used in the plan selection step of the registration flow.
 */

import { Card, CardHeader, CardBody, CardFooter, Button } from '@enfomentis/ui';
import { Check, Sparkles } from 'lucide-react';
import type { PlanConfig, BillingInterval } from '@enfomentis/shared/types';

interface PlanCardProps {
  plan: PlanConfig;
  billingInterval: BillingInterval;
  isSelected: boolean;
  onSelect: () => void;
  onContinue?: (() => void) | undefined;
}

export function PlanCard({
  plan,
  billingInterval,
  isSelected,
  onSelect,
  onContinue,
}: PlanCardProps) {
  const price = billingInterval === 'month' ? plan.monthly : plan.yearly;
  const isRecommended = plan.recommended;

  return (
    <Card
      isPressable
      isHoverable
      className={`relative transition-all duration-200 px-4 ${
        isSelected
          ? 'border-2 border-primary bg-primary-50/30 shadow-lg scale-[1.02]'
          : 'border border-default-200 hover:border-default-300 hover:shadow-md'
      } ${isRecommended ? 'ring-2 ring-primary ring-offset-2' : ''}`}
      onPress={onSelect}
    >
      {/* Badge */}
      {plan.badge && (
        <div className="absolute top-6 -right-10 rotate-45 z-10 bg-primary flex justify-center items-center gap-1 px-8 py-2 text-white">
          <Sparkles className="w-4 h-4" />
            <span className='text-sm'>{plan.badge}</span>
        </div>
      )}

      <CardHeader className="flex flex-col items-start gap-1 pt-6 pb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-xl font-bold text-text-primary">{plan.name}</h3>
          {isSelected && (
            <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
              <Check className="w-3 h-3 text-white" />
            </div>
          )}
        </div>
        <p className="text-sm text-text-secondary">{plan.description}</p>
      </CardHeader>

      <CardBody className="py-4">
        {/* Pricing */}
        <div className="mb-6">
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-text-primary">
              {price.displayPrice}
            </span>
            <span className="text-text-secondary">
              /{billingInterval === 'month' ? 'mo' : 'yr'}
            </span>
          </div>
          {billingInterval === 'year' && price.perMonthEquivalent && (
            <p className="text-sm text-success mt-1">
              {price.perMonthEquivalent} billed annually
            </p>
          )}
        </div>

        {/* Features */}
        <div className="space-y-3">
          {plan.features.map((feature, index) => (
            <div
              key={index}
              className={`flex items-start gap-3 ${
                feature.highlighted ? 'text-primary font-medium' : ''
              }`}
            >
              <div
                className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                  feature.highlighted
                    ? 'bg-primary text-white'
                    : 'bg-success-100 text-success'
                }`}
              >
                <Check className="w-3 h-3" />
              </div>
              <div>
                <p
                  className={`text-sm ${
                    feature.highlighted ? 'text-primary' : 'text-text-primary'
                  }`}
                >
                  {feature.name}
                </p>
                {feature.description && (
                  <p className="text-xs text-text-tertiary mt-0.5">
                    {feature.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardBody>

      <CardFooter className="pt-2 pb-4">
        <Button
          color={'primary'}
          variant={isSelected ? 'solid' : 'flat'}
          className="w-full"
          size="lg"
          onPress={onContinue || onSelect}
        >
          {isSelected ? 'Continue with ' + plan.name : 'Select ' + plan.name}
        </Button>
      </CardFooter>
    </Card>
  );
}
